// The prize-wheel desk, as this console can draw it.
//
// THE BROWSER DISPLAYS. Slice count and labels are saved here. The public
// page reads them on the wording route. resolveSpin lives on attend, not here.

import { PEOPLE_ENDPOINTS } from "../../../api/src/admin/endpoints.js";
import { WHEEL_RESULTS_ENDPOINT } from "../../../api/src/reports/endpoints.js";
import { authenticated, failureFor, type ApiOptions, type Failure } from "./client.js";
import type { SessionToken } from "../session/store.js";

export type WheelJoinKind = "registration" | "wheel";

export type WheelSlice = {
  readonly id: string;
  readonly label: string;
};

export type WheelDesk = {
  readonly activationId: string;
  readonly joinKind: WheelJoinKind;
  readonly slices: readonly WheelSlice[];
  readonly revisedAt: string | null;
};

export type WheelRead = { readonly ok: true; readonly value: WheelDesk } | ({ readonly ok: false } & Failure);

export async function readWheel(
  options: ApiOptions,
  token: SessionToken,
  activationId: string,
): Promise<WheelRead> {
  const answer = await authenticated(options, token, {
    path: `${PEOPLE_ENDPOINTS.wheel}?activation_id=${encodeURIComponent(activationId)}`,
    method: "GET",
  });
  if (!answer.ok) return { ok: false, ...failureFor(answer) };
  const desk = deskFrom(answer.body, activationId);
  if (desk === null) {
    return {
      ok: false,
      kind: "unavailable",
      detail:
        "The service answered a prize wheel this console could not read. Nothing is saved from it, because a " +
        "partly-read desk would look like the whole of it.",
    };
  }
  return { ok: true, value: desk };
}

export async function saveWheel(
  options: ApiOptions,
  token: SessionToken,
  input: { activationId: string; joinKind: WheelJoinKind; slices: readonly WheelSlice[] },
): Promise<WheelRead> {
  const answer = await authenticated(options, token, {
    path: PEOPLE_ENDPOINTS.wheel,
    method: "PUT",
    json: {
      activation_id: input.activationId,
      join_kind: input.joinKind,
      slices: input.slices.map((slice) => ({ id: slice.id, label: slice.label })),
    },
  });
  if (!answer.ok) return { ok: false, ...failureFor(answer) };
  const desk = deskFrom(answer.body, input.activationId);
  if (desk === null) {
    return {
      ok: false,
      kind: "unavailable",
      detail:
        "The service saved the prize wheel and then answered in a shape this console could not read. Open the page again.",
    };
  }
  return { ok: true, value: desk };
}

function deskFrom(body: unknown, activationId: string): WheelDesk | null {
  const row = fields(body);
  const joinKind = row["join_kind"] === "wheel" ? "wheel" : row["join_kind"] === "registration" ? "registration" : null;
  if (joinKind === null || !Array.isArray(row["slices"])) return null;
  const slices: WheelSlice[] = [];
  for (const item of row["slices"]) {
    const slice = sliceFrom(item);
    if (slice === null) return null;
    slices.push(slice);
  }
  if (slices.length < 4 || slices.length > 12) return null;
  const revised = row["revised_at"];
  return {
    activationId: text(row["activation_id"]) ?? activationId,
    joinKind,
    slices,
    revisedAt: typeof revised === "string" && revised !== "" ? revised : null,
  };
}

function sliceFrom(value: unknown): WheelSlice | null {
  const row = fields(value);
  const id = text(row["id"]);
  if (id === null) return null;
  return { id, label: typeof row["label"] === "string" ? row["label"] : "" };
}

function fields(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function text(value: unknown): string | null {
  return typeof value === "string" && value !== "" ? value : null;
}

// ------------------------------------------------- prizes, on top of the slices
//
// 156, decision C10. The Prizes desk reads every slice on the dial with its
// kind, weight, stock and what it has given; a save names every slice. The
// service decides who may (an Admin, an Editor for a client they hold) and this
// file draws its refusals in its own words.

export type SegmentKind = "prize" | "blank";
export type GoodUntil = "show_closes" | "7_days" | "30_days";

export type PrizeSegment = {
  readonly sliceId: string;
  readonly label: string;
  /** Null for a slice added on Setup since the prizes were saved: it has no weight yet. */
  readonly kind: SegmentKind | null;
  readonly weight: number;
  readonly stock: number | null;
  readonly given: number;
};

export type PrizeDesk = {
  readonly activationId: string;
  readonly configured: boolean;
  readonly weighted: boolean;
  readonly claimPrefix: string;
  readonly goodUntil: GoodUntil;
  readonly emailPrize: boolean;
  readonly closesOn: string | null;
  readonly segments: readonly PrizeSegment[];
  readonly revisedAt: string | null;
};

export type PrizeWrite = {
  readonly activationId: string;
  readonly weighted: boolean;
  readonly claimPrefix: string;
  readonly goodUntil: GoodUntil;
  readonly emailPrize: boolean;
  readonly segments: readonly { readonly sliceId: string; readonly kind: SegmentKind; readonly weight: number; readonly stock: number | null }[];
};

export type PrizeRead = { readonly ok: true; readonly value: PrizeDesk } | ({ readonly ok: false } & Failure);

export async function readPrizes(options: ApiOptions, token: SessionToken, activationId: string): Promise<PrizeRead> {
  const answer = await authenticated(options, token, {
    path: `${PEOPLE_ENDPOINTS.wheelPrizes}?activation_id=${encodeURIComponent(activationId)}`,
    method: "GET",
  });
  if (!answer.ok) return { ok: false, ...failureFor(answer) };
  const desk = prizeDeskFrom(answer.body, activationId);
  return desk === null ? { ok: false, ...unreadable("the wheel's prizes") } : { ok: true, value: desk };
}

export async function savePrizes(options: ApiOptions, token: SessionToken, input: PrizeWrite): Promise<PrizeRead> {
  const answer = await authenticated(options, token, {
    path: PEOPLE_ENDPOINTS.wheelPrizes,
    method: "PUT",
    json: {
      activation_id: input.activationId,
      weighted: input.weighted,
      claim_prefix: input.claimPrefix,
      good_until: input.goodUntil,
      email_prize: input.emailPrize,
      segments: input.segments.map((segment) => ({
        slice_id: segment.sliceId,
        kind: segment.kind,
        weight: segment.weight,
        stock: segment.stock,
      })),
    },
  });
  if (!answer.ok) return { ok: false, ...failureFor(answer) };
  const desk = prizeDeskFrom(answer.body, input.activationId);
  return desk === null ? { ok: false, ...unreadable("the saved prizes") } : { ok: true, value: desk };
}

function prizeDeskFrom(body: unknown, activationId: string): PrizeDesk | null {
  const row = fields(body);
  if (!Array.isArray(row["segments"])) return null;
  const segments: PrizeSegment[] = [];
  for (const item of row["segments"]) {
    const s = fields(item);
    const sliceId = text(s["slice_id"]);
    const kind = s["kind"] === "prize" || s["kind"] === "blank" ? s["kind"] : s["kind"] === null ? null : undefined;
    if (sliceId === null || kind === undefined || !isWhole(s["weight"]) || !isWhole(s["given"])) return null;
    if (s["stock"] !== null && !isWhole(s["stock"])) return null;
    segments.push({
      sliceId,
      label: typeof s["label"] === "string" ? s["label"] : "",
      kind,
      weight: s["weight"] as number,
      stock: s["stock"] === null ? null : (s["stock"] as number),
      given: s["given"] as number,
    });
  }
  const goodUntil = row["good_until"];
  if (goodUntil !== "show_closes" && goodUntil !== "7_days" && goodUntil !== "30_days") return null;
  if (typeof row["configured"] !== "boolean" || typeof row["weighted"] !== "boolean") return null;
  if (typeof row["email_prize"] !== "boolean" || typeof row["claim_prefix"] !== "string") return null;
  return {
    activationId: text(row["activation_id"]) ?? activationId,
    configured: row["configured"],
    weighted: row["weighted"],
    claimPrefix: row["claim_prefix"],
    goodUntil,
    emailPrize: row["email_prize"],
    closesOn: text(row["closes_on"]),
    segments,
    revisedAt: text(row["revised_at"]),
  };
}

// ------------------------------------------------------------------- results

export type WheelLane = "either" | "onsite" | "web";
export type WheelOutcome = "everything" | "prizes" | "blanks";

export type WheelSpin = {
  readonly spinId: string;
  readonly spunAt: string;
  readonly lane: "onsite" | "web" | null;
  readonly attendeeRef: string;
  readonly who: string | null;
  readonly landedOn: string;
  readonly prize: boolean;
  readonly claimCode: string | null;
  readonly claimExpiresAt: string | null;
  readonly expired: boolean;
  readonly collectedAt: string | null;
};

export type WheelFigures = {
  readonly spins: number;
  readonly onsite: number;
  readonly web: number;
  readonly prizes: number;
  readonly collected: number;
  readonly collectable: number;
  readonly expired: number;
};

export type WheelResults = {
  readonly figures: WheelFigures;
  readonly spins: readonly WheelSpin[];
  readonly matched: number;
};

export type ResultsRead = { readonly ok: true; readonly value: WheelResults } | ({ readonly ok: false } & Failure);

export async function readResults(
  options: ApiOptions,
  token: SessionToken,
  activationId: string,
  ask: { readonly lane: WheelLane; readonly outcome: WheelOutcome; readonly limit: number; readonly offset: number },
): Promise<ResultsRead> {
  const query = new URLSearchParams({
    lane: ask.lane,
    outcome: ask.outcome,
    limit: String(ask.limit),
    offset: String(ask.offset),
  });
  const answer = await authenticated(options, token, {
    path: `${WHEEL_RESULTS_ENDPOINT.replace(":activationId", encodeURIComponent(activationId))}?${query.toString()}`,
    method: "GET",
  });
  if (!answer.ok) return { ok: false, ...failureFor(answer) };
  const row = fields(answer.body);
  const figures = fields(row["figures"]);
  const page = fields(row["page"]);
  const numbers = ["spins", "onsite", "web", "prizes", "collected", "collectable", "expired"] as const;
  if (!numbers.every((key) => isWhole(figures[key])) || !isWhole(page["matched"]) || !Array.isArray(row["spins"])) {
    return { ok: false, ...unreadable("the wheel's results") };
  }
  const spins: WheelSpin[] = [];
  for (const item of row["spins"]) {
    const s = fields(item);
    const spinId = text(s["spin_id"]);
    const spunAt = text(s["spun_at"]);
    const attendeeRef = text(s["attendee_ref"]);
    if (spinId === null || spunAt === null || attendeeRef === null || typeof s["prize"] !== "boolean") {
      return { ok: false, ...unreadable("the wheel's results") };
    }
    spins.push({
      spinId,
      spunAt,
      lane: s["lane"] === "onsite" || s["lane"] === "web" ? s["lane"] : null,
      attendeeRef,
      who: text(s["who"]),
      landedOn: typeof s["landed_on"] === "string" ? s["landed_on"] : "",
      prize: s["prize"],
      claimCode: text(s["claim_code"]),
      claimExpiresAt: text(s["claim_expires_at"]),
      expired: s["expired"] === true,
      collectedAt: text(s["collected_at"]),
    });
  }
  return {
    ok: true,
    value: {
      figures: Object.fromEntries(numbers.map((key) => [key, figures[key] as number])) as WheelFigures,
      spins,
      matched: page["matched"] as number,
    },
  };
}

export type CollectRead =
  | { readonly ok: true; readonly value: { readonly spinId: string; readonly collectedAt: string } }
  | ({ readonly ok: false } & Failure);

export async function markCollected(
  options: ApiOptions,
  token: SessionToken,
  input: { readonly activationId: string; readonly spinId: string },
): Promise<CollectRead> {
  const answer = await authenticated(options, token, {
    path: PEOPLE_ENDPOINTS.wheelCollections,
    method: "POST",
    json: { activation_id: input.activationId, spin_id: input.spinId },
  });
  if (!answer.ok) return { ok: false, ...failureFor(answer) };
  const row = fields(answer.body);
  const collectedAt = text(row["collected_at"]);
  if (collectedAt === null) return { ok: false, ...unreadable("the collection") };
  return { ok: true, value: { spinId: text(row["spin_id"]) ?? input.spinId, collectedAt } };
}

function unreadable(what: string): Failure {
  return {
    kind: "unavailable",
    detail: `The service answered ${what} in a shape this console could not read, so nothing is drawn from it.`,
  };
}

function isWhole(value: unknown): value is number {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 0;
}
